import {
   CALENDAR_LOCAL_ECHO_TTL_MS,
   TombstoneRegistry,
} from "../../sync/tombstones";
import { createStorePatchBus } from "./calendarStorePatchBus";
import {
   CALENDAR_STORE_VERSION,
   EMPTY_SELECTION,
   EMPTY_VIEWPORT,
} from "./calendarStoreTypes";
import { makeSlotKey, normalizeEventId, readEventId } from "./eventIdentity";

function cloneSelection(selection) {
   return {
      slotKeys: Array.isArray(selection?.slotKeys)
         ? [...selection.slotKeys]
         : [],
      reservationId: String(selection?.reservationId || ""),
      anchorSlotKey: String(selection?.anchorSlotKey || ""),
   };
}

function cloneViewport(viewport) {
   return {
      monthKey: String(viewport?.monthKey || ""),
      scrollTop: Math.max(0, Number(viewport?.scrollTop) || 0),
      scrollLeft: Math.max(0, Number(viewport?.scrollLeft) || 0),
      fastScroll: viewport?.fastScroll === true,
      panning: viewport?.panning === true,
   };
}

/**
 * Single source of truth for Master Calendar event layout + UI selection.
 * Pure store — no React, no Redux. Subscribers drive rendering.
 */
export class CalendarStore {
   constructor() {
      this._version = 0;
      this._dataVersion = 0;
      this._monthKey = "";
      this._lastServerSyncAt = "";
      this._eventsById = new Map();
      this._slotsByKey = new Map();
      this._eventSlotById = new Map();
      this._tombstones = new TombstoneRegistry();
      this._selection = cloneSelection(EMPTY_SELECTION);
      this._viewport = cloneViewport(EMPTY_VIEWPORT);
      this._listeners = new Set();
      this._pendingCommands = new Map();
      /** @type {Map<string, number>} eventId → echo expiry timestamp */
      this._localEchoUntil = new Map();
      this._patchBus = createStorePatchBus();
   }

   static LOCAL_ECHO_DEFAULT_MS = CALENDAR_LOCAL_ECHO_TTL_MS;

   get patchBus() {
      return this._patchBus;
   }

   pushSlotPatch(patch) {
      this._patchBus.push(patch);
   }

   clearSlotPatches() {
      this._patchBus.clear();
   }

   getSnapshot() {
      return {
         version: this._version,
         dataVersion: this._dataVersion,
         storeVersion: CALENDAR_STORE_VERSION,
         monthKey: this._monthKey,
         lastServerSyncAt: this._lastServerSyncAt,
         eventsById: this._eventsById,
         slotsByKey: this._slotsByKey,
         tombstones: this._tombstones,
         selection: this._selection,
         viewport: this._viewport,
         pendingCommandCount: this._pendingCommands.size,
      };
   }

   subscribe(listener) {
      this._listeners.add(listener);
      return () => {
         this._listeners.delete(listener);
      };
   }

   _notify() {
      this._version += 1;
      this._dataVersion += 1;
      for (const listener of this._listeners) {
         listener();
      }
   }

   // UI-only mutations (selection / viewport). Subscribers are still notified,
   // but dataVersion stays put so the month rows pipeline does not rebuild.
   _notifyUi() {
      this._version += 1;
      for (const listener of this._listeners) {
         listener();
      }
   }

   _setEventSlot(eventId, slotKey, eventRow) {
      const id = normalizeEventId(eventId);
      if (!id) return;

      const prevSlot = this._eventSlotById.get(id);
      if (prevSlot && prevSlot !== slotKey) {
         const prevIds = this._slotsByKey.get(prevSlot);
         if (Array.isArray(prevIds)) {
            const nextIds = prevIds.filter((value) => value !== id);
            if (nextIds.length) this._slotsByKey.set(prevSlot, nextIds);
            else this._slotsByKey.delete(prevSlot);
         }
      }

      if (!slotKey) {
         this._eventsById.delete(id);
         this._eventSlotById.delete(id);
         return;
      }

      this._eventsById.set(id, eventRow);
      this._eventSlotById.set(id, slotKey);
      const current = this._slotsByKey.get(slotKey);
      if (!Array.isArray(current)) {
         this._slotsByKey.set(slotKey, [id]);
         return;
      }
      if (!current.includes(id)) current.push(id);
   }

   _removeEvent(eventId, { tombstone = false } = {}) {
      const id = normalizeEventId(eventId);
      if (!id) return;
      const slotKey = this._eventSlotById.get(id) || "";
      if (slotKey) {
         const ids = this._slotsByKey.get(slotKey);
         if (Array.isArray(ids)) {
            const nextIds = ids.filter((value) => value !== id);
            if (nextIds.length) this._slotsByKey.set(slotKey, nextIds);
            else this._slotsByKey.delete(slotKey);
         }
      }
      this._eventsById.delete(id);
      this._eventSlotById.delete(id);
      if (tombstone) this._tombstones.mark(id);
   }

   resetMonth(monthKey = "") {
      this._monthKey = String(monthKey || "").trim();
      this._eventsById.clear();
      this._slotsByKey.clear();
      this._eventSlotById.clear();
      this._pendingCommands.clear();
      this._localEchoUntil.clear();
      this._patchBus.clear();
      this._tombstones.prune();
      this._notify();
   }

   _pruneLocalEcho() {
      const now = Date.now();
      for (const [id, until] of this._localEchoUntil.entries()) {
         if (!Number.isFinite(until) || until <= now) {
            this._localEchoUntil.delete(id);
         }
      }
   }

   markLocalEcho(eventId, durationMs = CalendarStore.LOCAL_ECHO_DEFAULT_MS) {
      const id = normalizeEventId(eventId);
      const ms = Number(durationMs);
      if (!id || !Number.isFinite(ms) || ms <= 0) return;
      this._localEchoUntil.set(id, Date.now() + ms);
   }

   clearLocalEcho(eventId) {
      this._localEchoUntil.delete(normalizeEventId(eventId));
   }

   isLocalEchoProtected(eventId) {
      this._pruneLocalEcho();
      const id = normalizeEventId(eventId);
      if (!id) return false;
      const until = this._localEchoUntil.get(id);
      return Number.isFinite(until) && until > Date.now();
   }

   _isProtectedFromHydrateRemoval(eventId) {
      const id = normalizeEventId(eventId);
      if (!id) return false;
      return this._pendingCommands.has(id) || this.isLocalEchoProtected(id);
   }

   // Set-equality over (id → slotKey) pairs. Replaces the old sorted-string
   // signature: O(n) with no sorting and no string building, same semantics.
   _eventLayoutChangedSince(beforeSlotById) {
      if (beforeSlotById.size !== this._eventSlotById.size) return true;
      for (const [id, slotKey] of this._eventSlotById.entries()) {
         if (beforeSlotById.get(id) !== slotKey) return true;
      }
      return false;
   }

   /**
    * Authoritative server hydration. Replaces in-memory month layout while
    * preserving tombstoned ids and in-flight optimistic commands.
    */
   hydrateFromServer({
      monthKey = "",
      items = [],
      serverTime = "",
      slotResolver = null,
   } = {}) {
      const slotByIdBefore = new Map(this._eventSlotById);
      const nextMonthKey = String(monthKey || "").trim();
      if (nextMonthKey && nextMonthKey !== this._monthKey) {
         if (this._pendingCommands.size === 0 && this._localEchoUntil.size === 0) {
            this._eventsById.clear();
            this._slotsByKey.clear();
            this._eventSlotById.clear();
         } else {
            for (const [id] of [...this._eventsById.entries()]) {
               if (!this._isProtectedFromHydrateRemoval(id)) {
                  this._removeEvent(id, { tombstone: false });
               }
            }
         }
      }
      this._monthKey = nextMonthKey;
      if (serverTime) this._lastServerSyncAt = String(serverTime);

      const incomingIds = new Set();
      const rows = Array.isArray(items) ? items : [];

      for (const row of rows) {
         const id = readEventId(row);
         if (!id || this._tombstones.has(id)) continue;
         // Never overwrite rows with in-flight optimistic commands.
         if (this._pendingCommands.has(id)) continue;
         incomingIds.add(id);
         this.clearLocalEcho(id);

         const slotKey =
            typeof slotResolver === "function"
               ? String(slotResolver(row) || "").trim()
               : String(row?.__slotKey || "").trim();
         if (!slotKey) continue;
         this._setEventSlot(id, slotKey, row);
      }

      for (const [id] of this._eventsById.entries()) {
         if (!incomingIds.has(id) && !this._isProtectedFromHydrateRemoval(id)) {
            this._removeEvent(id);
         }
      }

      if (this._eventLayoutChangedSince(slotByIdBefore)) {
         this._notify();
      }
   }

   upsertEvent({ event, slotKey, pendingCommandId = "" } = {}) {
      const id = readEventId(event);
      const key = String(slotKey || "").trim();
      if (!id || !key) return false;
      if (this._tombstones.has(id)) return false;

      this._setEventSlot(id, key, event);
      if (pendingCommandId) {
         this._pendingCommands.set(id, String(pendingCommandId));
      }
      this._notify();
      return true;
   }

   removeEvent(eventId, { tombstone = true, clearPending = true } = {}) {
      const id = normalizeEventId(eventId);
      if (!id) return false;
      this._removeEvent(id, { tombstone });
      if (clearPending) this._pendingCommands.delete(id);
      this.clearLocalEcho(id);
      this._notify();
      return true;
   }

   replaceEventId(fromId, toId, nextEvent = null, slotKey = "") {
      const from = normalizeEventId(fromId);
      const to = normalizeEventId(toId);
      if (!from || !to) return false;

      // Never resurrect a reservation the user already deleted.
      if (this._tombstones.has(to)) {
         this._removeEvent(from, { tombstone: true });
         this._pendingCommands.delete(from);
         this.clearLocalEcho(from);
         this._notify();
         return false;
      }

      // tmp already gone:
      // - successful prior promotion (tmp tombstoned, real id lives) → no-op
      // - user deleted optimistic create before confirm → keep both deleted
      if (this._tombstones.has(from) && !this._eventsById.has(from)) {
         if (this._eventsById.has(to)) {
            this._pendingCommands.delete(from);
            this.clearLocalEcho(from);
            return true;
         }
         this._tombstones.mark(to);
         this._removeEvent(to, { tombstone: true });
         this._pendingCommands.delete(from);
         this._pendingCommands.delete(to);
         this.clearLocalEcho(from);
         this.clearLocalEcho(to);
         this._notify();
         return false;
      }

      const existing = this._eventsById.get(from);
      const resolvedSlot =
         String(slotKey || "").trim() ||
         this._eventSlotById.get(from) ||
         "";
      const row = nextEvent || existing;
      if (!row || !resolvedSlot) return false;

      this._removeEvent(from, { tombstone: true });
      this._setEventSlot(to, resolvedSlot, row);
      if (this._pendingCommands.has(from)) {
         const cmd = this._pendingCommands.get(from);
         this._pendingCommands.delete(from);
         this._pendingCommands.set(to, cmd);
      }
      this._notify();
      return true;
   }

   markPendingCommand(eventId, commandId) {
      const id = normalizeEventId(eventId);
      const cmd = String(commandId || "").trim();
      if (!id || !cmd) return;
      this._pendingCommands.set(id, cmd);
   }

   clearPendingCommand(eventId) {
      const id = normalizeEventId(eventId);
      if (!id) return;
      this._pendingCommands.delete(id);
   }

   clearTombstone(eventId) {
      this._tombstones.clear(normalizeEventId(eventId));
   }

   getEventSlotKey(eventId) {
      return this._eventSlotById.get(normalizeEventId(eventId)) || "";
   }

   isTombstoned(eventId) {
      return this._tombstones.has(normalizeEventId(eventId));
   }

   getEventsList() {
      return Array.from(this._eventsById.values());
   }

   getEventById(eventId) {
      return this._eventsById.get(normalizeEventId(eventId)) || null;
   }

   getSlotEventIds(slotKey) {
      const ids = this._slotsByKey.get(String(slotKey || "").trim());
      return Array.isArray(ids) ? [...ids] : [];
   }

   setSelection(patch = {}) {
      this._selection = {
         slotKeys: Array.isArray(patch.slotKeys)
            ? patch.slotKeys.map((key) => String(key))
            : this._selection.slotKeys,
         reservationId:
            patch.reservationId != null
               ? String(patch.reservationId)
               : this._selection.reservationId,
         anchorSlotKey:
            patch.anchorSlotKey != null
               ? String(patch.anchorSlotKey)
               : this._selection.anchorSlotKey,
      };
      this._notifyUi();
   }

   clearSelection() {
      this._selection = cloneSelection(EMPTY_SELECTION);
      this._notifyUi();
   }

   setViewport(patch = {}) {
      const prev = this._viewport;
      const next = {
         monthKey:
            patch.monthKey != null
               ? String(patch.monthKey)
               : prev.monthKey,
         scrollTop:
            patch.scrollTop != null
               ? Math.max(0, Number(patch.scrollTop) || 0)
               : prev.scrollTop,
         scrollLeft:
            patch.scrollLeft != null
               ? Math.max(0, Number(patch.scrollLeft) || 0)
               : prev.scrollLeft,
         fastScroll:
            patch.fastScroll != null
               ? patch.fastScroll === true
               : prev.fastScroll,
         panning:
            patch.panning != null ? patch.panning === true : prev.panning,
      };
      const shouldNotify =
         next.fastScroll !== prev.fastScroll ||
         next.panning !== prev.panning ||
         next.monthKey !== prev.monthKey;
      this._viewport = next;
      if (shouldNotify) {
         this._notifyUi();
      }
   }
}

export function createCalendarStore() {
   return new CalendarStore();
}

export { makeSlotKey, readEventId };
